"use client";

import { useTranslations, useLocale } from "next-intl";
import { Lock, ArrowRight } from "lucide-react";
import { TeacherAvatar } from "./TeacherAvatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface Teacher {
  id: string;
  name: string;
  avatar: string;
  tags: string[];
  description: string;
  unlocked: boolean;
  requiredPlanCode: string | null;
  route: string;
}

interface TeacherCardProps {
  teacher: Teacher;
}

export function TeacherCard({ teacher }: TeacherCardProps) {
  const t = useTranslations("teachers");
  const locale = useLocale();

  return (
    <div
      className={cn(
        "group relative flex flex-col rounded-2xl border bg-card p-6 shadow-sm transition hover:shadow-md",
        !teacher.unlocked && "opacity-90",
      )}
    >
      <div className="mb-4 flex items-start justify-between">
        <TeacherAvatar
          id={teacher.id}
          name={t(`teacher.${teacher.id}.name`)}
          avatar={teacher.avatar}
          size={96}
        />
        {!teacher.unlocked && (
          <span className="rounded-full bg-muted p-2 text-muted-foreground">
            <Lock className="h-4 w-4" />
          </span>
        )}
      </div>

      <h3 className="mb-1 text-xl font-semibold">
        {t(`teacher.${teacher.id}.name`)}
      </h3>

      <div className="mb-3 flex flex-wrap gap-2">
        {teacher.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
          >
            {t(`tags.${tag}`)}
          </span>
        ))}
      </div>

      <p className="mb-6 flex-1 text-sm text-muted-foreground">
        {t(`teacher.${teacher.id}.description`)}
      </p>

      <Button
        asChild
        variant={teacher.unlocked ? "outline" : "outline"}
        className={cn(
          "w-full",
          teacher.unlocked &&
            "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100 hover:text-emerald-800",
        )}
        disabled={!teacher.unlocked}
      >
        <a href={`/${locale}${teacher.unlocked ? teacher.route : "/pricing"}`}>
          {teacher.unlocked ? (
            <>
              {t("status.unlocked")} <ArrowRight className="ml-2 h-4 w-4" />
            </>
          ) : (
            t("status.buy")
          )}
        </a>
      </Button>
    </div>
  );
}
